import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { useOrderAuth } from "./OrderAuthContext";
import orderData from "../Datas/orderData";
import productData from "../Datas/productData";
import "../css/orders.scss";

const SELLER_NAME = "mvandora";
const SELLER_URL = "https://mvandora.store";

function formatDate(value) {
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(new Date(`${value}T00:00:00`));
}

function formatMoney(amount, currency) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

function orderReference(order) {
  return `ORD-${order.createdAt.replaceAll("-", "")}-${order.id.replace(
    "order-",
    ""
  )}`;
}

function OrderDetail() {
  const navigate = useNavigate();
  const { logout } = useOrderAuth();
  const { id } = useParams();
  const order = orderData.find((item) => item.id === id);
  const product = productData.find((item) => item.id === order?.productId);

  const handleLogout = () => {
    logout();
    navigate("/order-login", { replace: true });
  };

  if (!order) {
    return (
      <div className="orders-page">
        <Header />
        <main className="order-detail__not-found" id="MainContent">
          <p className="orders-page__eyebrow">Order management</p>
          <h1>Order not found</h1>
          <p>The order reference does not match an available record.</p>
          <Link to="/orders">Back to orders</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const unitPrice = order.totalAmount / order.quantity;

  return (
    <div className="orders-page">
      <Header />
      <main className="order-record" id="MainContent">
        <div className="order-record__topbar">
          <Link to="/orders">← Back to orders</Link>
          <span>{orderReference(order)}</span>
          <button type="button" onClick={handleLogout}>
            Sign out
          </button>
        </div>

        <section className="seller-record" aria-labelledby="seller-name">
          <div className="seller-record__identity">
            <span className="seller-record__icon" aria-hidden="true">↗</span>
            <div>
              <span>Seller store</span>
              <h1 id="seller-name">{SELLER_NAME}</h1>
              <p>This order is linked to the seller record.</p>
            </div>
          </div>
          <a
            className="seller-record__visit"
            href={SELLER_URL}
            target="_blank"
            rel="noreferrer"
          >
            mvandora.store <span aria-hidden="true">↗</span>
          </a>
        </section>

        <div className="order-record__grid">
          <section className="record-card record-card--summary">
            <header className="record-card__header">
              <span className="record-card__icon" aria-hidden="true">◇</span>
              <h2>Order summary</h2>
            </header>

            <div className="record-product">
              {product ? (
                <Link to={`/shopDetails/${product.id}`}>
                  <img src={product.image} alt={product.name} />
                </Link>
              ) : null}
              <div className="record-product__copy">
                <span>Footwear</span>
                <h3>{product?.name || order.productName}</h3>
                <p>
                  Quantity: {order.quantity} {order.quantity === 1 ? "pair" : "pairs"}
                </p>
                <p>Unit price: {formatMoney(unitPrice, order.currency)} per pair</p>
              </div>
              <strong className="record-product__total">
                {formatMoney(order.totalAmount, order.currency)}
              </strong>
            </div>

            <dl className="record-totals">
              <div>
                <dt>Items</dt>
                <dd>{order.quantity}</dd>
              </div>
              <div>
                <dt>Unit price</dt>
                <dd>{formatMoney(unitPrice, order.currency)}</dd>
              </div>
              <div className="record-totals__grand">
                <dt>Total</dt>
                <dd>{formatMoney(order.totalAmount, order.currency)}</dd>
              </div>
            </dl>
          </section>

          <section className="record-card record-card--delivery">
            <header className="record-card__header">
              <span className="record-card__icon" aria-hidden="true">⌖</span>
              <h2>Delivery address</h2>
            </header>

            <div className="delivery-recipient">
              <h3>{order.buyerName}</h3>
              <address>{order.shippingAddress.join(", ")}</address>
              <p>Ordered {formatDate(order.createdAt)}</p>
            </div>

            <dl className="delivery-meta">
              <div>
                <dt>Carrier</dt>
                <dd>{order.carrier}</dd>
              </div>
              <div>
                <dt>Tracking</dt>
                <dd>{order.trackingNumber}</dd>
              </div>
              <div>
                <dt>Courier status</dt>
                <dd>Delivered</dd>
              </div>
            </dl>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default OrderDetail;
